import {Button} from '@kit';

import React, {useCallback, useEffect, useState} from 'react';
import {useApolloClient} from '@apollo/client/react';
import {browserSupportsWebAuthnAutofill, startAuthentication, WebAuthnAbortService} from '@simplewebauthn/browser';

import {
	OauthUrlDocument,
	OauthUrlQuery,
	useOauthProvidersQuery,
	usePasskeyLoginMutation,
	usePasskeyLoginOptionsMutation,
	User,
} from '../../generated/types';
import {useUser} from '../use-user';
import {YandexIcon} from './yandex-icon';
import {getError} from '../utils';

import css from './social-login.module.css';

export const PROVIDER_NAMES: Record<string, string> = {yandex: 'Яндекс'};

const PROVIDER_ICONS: Record<string, React.ReactNode> = {yandex: <YandexIcon />};

const PASSKEY_IDS_KEY = 'passkey-ids';

/** Идентификаторы ключей, которыми в этом браузере входили или которые добавляли: сайту браузер сам не говорит, есть ли ключ на устройстве */
export function localPasskeyIds(): string[] {
	try {
		const ids = JSON.parse(window.localStorage.getItem(PASSKEY_IDS_KEY) || '[]');
		return Array.isArray(ids) ? ids : [];
	} catch {
		return [];
	}
}

/** Запоминает ключ: по нему окно входа сразу предлагает ключ, а профиль не предлагает добавить его повторно */
export function rememberPasskey(id: string) {
	try {
		const ids = localPasskeyIds();
		if (!ids.includes(id)) window.localStorage.setItem(PASSKEY_IDS_KEY, JSON.stringify([...ids, id]));
	} catch {
		// хранилище может быть недоступно: подсказки просто не будет
	}
}

/** Ключ удалён на сайте: забываем его в браузере и просим устройство убрать его из списка (если браузер это умеет) */
export function forgetPasskey(id: string) {
	try {
		window.localStorage.setItem(PASSKEY_IDS_KEY, JSON.stringify(localPasskeyIds().filter((item) => item !== id)));
	} catch {
		// хранилище может быть недоступно
	}
	(window.PublicKeyCredential as any)
		?.signalUnknownCredential?.({rpId: window.location.hostname, credentialId: id})
		?.catch?.(() => undefined);
}

const hasPasskeyHint = () => localPasskeyIds().length > 0;

type OAuthButtonsProps = {
	/** «Войти через» или «Привязать» */
	action: string;
	onError: (message: string) => void;
	/** провайдеры, которые не нужно показывать (например, уже привязанные) */
	exclude?: string[];
};

/** Кнопки входа через сервисы. Показываются только для тех, у кого в API заданы ключи; список берётся в браузере, чтобы html оставался общим для всех */
export const OAuthButtons: React.FC<OAuthButtonsProps> = ({action, onError, exclude = []}) => {
	const client = useApolloClient();
	const {data} = useOauthProvidersQuery({ssr: false});
	const [pending, setPending] = useState<string>();

	const providers = (data?.oauthProviders || []).filter((provider) => !exclude.includes(provider));
	if (!providers.length) return null;

	const start = async (provider: string) => {
		setPending(provider);
		try {
			const result = await client.query<OauthUrlQuery>({
				query: OauthUrlDocument,
				variables: {provider},
				fetchPolicy: 'network-only',
			});
			if (result.data?.oauthUrl) {
				window.location.href = result.data.oauthUrl;
				return;
			}
			onError(getError(result.error)?.message || 'Не удалось начать вход');
		} catch (error) {
			onError(getError(error as any)?.message || 'Не удалось начать вход');
		}
		setPending(undefined);
	};

	return (
		<div className={css.buttons}>
			{providers.map((provider) => (
				<Button
					key={provider}
					appearance="outline"
					icon={PROVIDER_ICONS[provider]}
					loading={pending === provider}
					onClick={() => start(provider)}
				>
					{action} {PROVIDER_NAMES[provider] || provider}
				</Button>
			))}
		</div>
	);
};

type PasskeyLoginOptions = {
	onSuccess?: () => void;
	onError: (message: string) => void;
};

/**
 * Вход по ключу доступа.
 * `login(false)` открывает системное окно выбора ключа, `login(true)` ждёт, пока пользователь выберет ключ
 * в подсказке поля почты (условный режим WebAuthn): браузер сам не сообщает сайту, есть ли у пользователя ключ.
 */
export function usePasskeyLogin({onSuccess, onError}: PasskeyLoginOptions) {
	const [supported, setSupported] = useState(false);
	const [autofillSupported, setAutofillSupported] = useState(false);
	const [loading, setLoading] = useState(false);
	const [getOptions] = usePasskeyLoginOptionsMutation();
	const [passkeyLogin] = usePasskeyLoginMutation();
	const {setUser} = useUser();

	useEffect(() => {
		setSupported(!!window.PublicKeyCredential);
		browserSupportsWebAuthnAutofill()
			.then(setAutofillSupported)
			.catch(() => setAutofillSupported(false));
	}, []);

	const login = useCallback(
		async (conditional: boolean) => {
			if (!conditional) setLoading(true);
			let credentialId = '';
			try {
				const options = await getOptions();
				if (!options.data?.passkeyLoginOptions) throw new Error('Не удалось начать вход по ключу');
				const response = await startAuthentication({
					optionsJSON: options.data.passkeyLoginOptions,
					useBrowserAutofill: conditional,
				});
				credentialId = response.id;
				const result = await passkeyLogin({variables: {response}});
				if (!result.data?.passkeyLogin) throw result.error || new Error('Не удалось войти по ключу');
				rememberPasskey(response.id);
				setUser(result.data.passkeyLogin as User);
				onSuccess?.();
			} catch (error: any) {
				// закрытое пользователем окно и отменённый запрос — не ошибки
				if (error?.name !== 'NotAllowedError' && error?.name !== 'AbortError') {
					const parsed = getError(error);
					if (parsed?.code === 'PASSKEY_UNKNOWN' && credentialId) forgetPasskey(credentialId);
					onError(parsed?.message || 'Не удалось войти по ключу');
				}
			}
			setLoading(false);
		},
		[getOptions, passkeyLogin, setUser, onSuccess, onError],
	);

	return {supported, autofillSupported, loading, login};
}

/** Пока окно входа открыто: если на устройстве уже входили по ключу, сразу предлагаем его, иначе ключи подсказываются в поле почты */
export function useAutoPasskeyLogin(options: PasskeyLoginOptions) {
	const passkey = usePasskeyLogin(options);
	const {supported, autofillSupported, login} = passkey;

	useEffect(() => {
		if (!supported) return;
		if (hasPasskeyHint()) login(false);
		else if (autofillSupported) login(true);
		return () => WebAuthnAbortService.cancelCeremony();
		// запускаем один раз, когда стало известно, что браузер умеет ключи
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [supported, autofillSupported]);

	return passkey;
}

type PasskeyLoginButtonProps = {
	loading: boolean;
	onClick: () => void;
};

/** Кнопка входа по ключу */
export const PasskeyLoginButton: React.FC<PasskeyLoginButtonProps> = ({loading, onClick}) => (
	<Button appearance="outline" loading={loading} onClick={onClick}>
		Войти по ключу
	</Button>
);
